import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { UseDisclosureReturn, useDisclosure } from "@chakra-ui/react";
import { KGStepInterface } from "pages/create/KGPlan/Interface";
import {
  Header,
  MetricProto,
  ModelInfo,
  ObservationColumnProto,
  FilterProto,
} from "build-ts/metadata";
import {
  AnalysisStepProto,
  AnalysisPlanProto,
  ObservationEditProto,
  KGObservationProto,
  RecommendationRuleProto,
  RecommendationProto,
  ObservationEditType
} from "build-ts/analysis_plan";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";
import { ColumnHashProto } from "build-ts/data";
import { v4 as uuidv4 } from "uuid";
import { MetadataTypeEnum, ColumnType, AnalysisColumnType } from "build-ts/enums";
import { getNodeData, statefulAnalysisTree } from "services/AnalysisTreeServices";
import { StatefulRequestBuilder } from "containers/stateful/StatefulRequests";
import { ObservationUtils } from "utils/ObservationUtils";
import { buildNodeMapFromPlan } from "pages/create/KGPlan/KGAnalysisPlan";
import showToast from "hooks/useCustomToast";
import { getMetricById } from "services/MetricService";
import { createOrUpdatePlan, getPlan } from "services/AnalysisPlanServices";
import { AnalysisActionRequest, ActionRequest } from "build-ts/jobs_interface";
import { useKGBatchContext } from "./KGBatchContext";
import { createOrUpdateJob as createOrUpdateJobApi } from "../../services/BatchServices";
import { JobRequest } from "build-ts/jobs_interface";
import { AnalysisTreeNodeProto, CommentaryProto } from "build-ts/analysis";
import { GenerateTree_Request, StatefulAnalysisTreeRequest, StatefulResponse } from "build-ts/stateful_api";
import { AnalysisTreeRequestType } from "build-ts/stateful_api";
import { NodeInsights } from "containers/Interfaces";
import { mergeMetricEdgesByType } from "utils/analysisUtils";

interface KGAnalysisPlanContextType {
  plan: KGStepInterface | null;
  setPlan: React.Dispatch<React.SetStateAction<KGStepInterface | null>>;
  selectedNodeId: string | null;
  setSelectedNodeId: React.Dispatch<React.SetStateAction<string | null>>;
  configurator: UseDisclosureReturn;
  updateNode: (updatedNode: KGStepInterface) => void;
  addChildToRecommendation: (
    nodeId: string,
    recommendationIndex: number,
    newChild: KGStepInterface
  ) => void;
  deleteNode: (nodeId: string) => void;
  deleteChildrenOfRecommendation: (
    nodeId: string,
    recommendationIndex: number
  ) => void;
  nodeMap: Map<string, KGStepInterface>;
  setNodeMap: React.Dispatch<React.SetStateAction<Map<string, KGStepInterface>>>;
  updateNodeMap: (node: KGStepInterface) => void;
  metrics: Map<string, MetricProto>;
  setMetrics: React.Dispatch<React.SetStateAction<Map<string, MetricProto>>>;
  models: Map<string, ModelInfo>;
  setModels: React.Dispatch<React.SetStateAction<Map<string, ModelInfo>>>;
  selectedMetricHeader: Header[];
  setSelectedMetricHeader: React.Dispatch<React.SetStateAction<Header[]>>;
  metricList: Header[];
  executeKGAnalysisPlanHandler: () => void;
  setMetricList: React.Dispatch<React.SetStateAction<Header[]>>;
  getKGObservationFromKGStepInterface: (nodeId: string) => KGObservationProto;
  handleLoadPlan: (action: AnalysisActionRequest) => void;
  saveKGAnalysisPlanHandler: () => void;
  duplicatePlan: (planId: string) => Promise<Header | null>;
  selectedStepIndex: number | null;
  analysisActionRequests: AnalysisActionRequest[];
  // Generic node save function
  handleNodeSave: (
    nodeId: string,
    nodeData: {
      nodeName: string;
      selectedColumns: ObservationColumnProto[];
      selectedBenchmarks: BenchmarkEntityProto[];
      filters: FilterProto[];
      analysisColumns: ColumnHashProto[];
      recommendationRules: RecommendationRuleProto[];
      selectedMetrics?: MetricProto[];
    },
    latestRecommendations?: RecommendationRuleProto[],
    updatedNode?: KGStepInterface
  ) => KGStepInterface | null;
  // Observation data state and handlers
  observationData: NodeInsights | null;
  setObservationData: React.Dispatch<React.SetStateAction<NodeInsights | null>>;
  currentPlanHeader: Header | undefined;
  setCurrentPlanHeader: React.Dispatch<React.SetStateAction<Header | undefined>>;
  handleGetDataForNode: (nodeId: string) => Promise<void>;
  handleAddChildNode: (recommendationIndex: number, currentNode: KGStepInterface) => Promise<void>;
  // Generate all column hash combinations function
  generateAllColumnHashes: (
    currentNode: KGStepInterface,
    selectedColumns: ObservationColumnProto[],
    selectedMetrics: MetricProto[],
    selectedBenchmarks: BenchmarkEntityProto[],
    analysisColumns: ColumnHashProto[]
  ) => ColumnHashProto[];
  // Recommendation popover state management
  openRecommendationNodeId: string | null;
  setOpenRecommendationNodeId: React.Dispatch<React.SetStateAction<string | null>>;
  openRecommendationPopover: (nodeId: string) => void;
  closeRecommendationPopover: () => void;
  getDescendantNodeIds: (rootId: string) => string[];
}

const KGAnalysisPlanContext = createContext<KGAnalysisPlanContextType | undefined>(undefined);

export const useKGAnalysisPlanContext = () => {
  const context = useContext(KGAnalysisPlanContext);
  if (!context) {
    throw new Error(
      "useKGAnalysisPlanContext must be used within an KGAnalysisPlanContextProvider"
    );
  }
  return context;
};

// Helper function to update a node.
const updateNode = (
  nodeMap: Map<string, KGStepInterface>,
  updatedNode: KGStepInterface
): Map<string, KGStepInterface> => {
  const newMap = new Map(nodeMap);
  newMap.set(updatedNode.id, updatedNode);
  return newMap;
};

// Helper function to add a child to a recommendation.
const addChildToRecommendation = (
  nodeMap: Map<string, KGStepInterface>,
  nodeId: string,
  recommendationIndex: number,
  newChild: KGStepInterface
): Map<string, KGStepInterface> => {
  const newMap = new Map(nodeMap);
  // Add the new child to the map
  newMap.set(newChild.id, newChild);
  // Get the node we're adding a recommendation child to.
  const node = newMap.get(nodeId);
  if (!node) return newMap;
  // Create or update the recommendation_children_map
  const recommendationChildrenMap =
    node.recommendation_children_map || new Map<number, string[]>();
  const children = recommendationChildrenMap.get(recommendationIndex) || [];
  recommendationChildrenMap.set(recommendationIndex, [
    ...children,
    newChild.id,
  ]);
  // Update the node with the new recommendation_children_map
  const updatedNode = {
    ...node,
    recommendation_children_map: recommendationChildrenMap,
  };
  newMap.set(nodeId, updatedNode);
  return newMap;
};

// Helper function to find the parent node of a given node
const findParentNode = (
  nodeMap: Map<string, KGStepInterface>,
  childId: string
): { parentNode: KGStepInterface | null; recommendationIndex: number } => {
  // Use Array.from to convert the Map entries to an array that can be safely iterated
  for (const [, node] of Array.from(nodeMap.entries())) {
    if (node.recommendation_children_map) {
      for (const [index, childIds] of Array.from(
        node.recommendation_children_map.entries()
      )) {
        if (childIds.includes(childId)) {
          return { parentNode: node, recommendationIndex: index };
        }
      }
    }
  }
  return { parentNode: null, recommendationIndex: -1 };
};

// Helper function to get all descendant node IDs (entire subtree)
const getDescendantNodeIds = (
  nodeMap: Map<string, KGStepInterface>,
  rootId: string
): string[] => {
  const nodeIds: string[] = [];
  const nodeToDelete = nodeMap.get(rootId);

  if (!nodeToDelete || !nodeToDelete.recommendation_children_map) {
    return nodeIds;
  }
  // Collect all direct child IDs from all recommendations
  Array.from(nodeToDelete.recommendation_children_map.entries()).forEach(
    ([, childIds]) => {
      // Add direct children
      nodeIds.push(...childIds);
      // Recursively add descendants of each child
      childIds.forEach((childId) => {
        nodeIds.push(...getDescendantNodeIds(nodeMap, childId));
      });
    }
  );

  return nodeIds;
};

// Helper function to delete a node and its entire subtree
const deleteNode = (
  nodeMap: Map<string, KGStepInterface>,
  nodeId: string
): Map<string, KGStepInterface> => {
  // Cannot delete the root node
  if (nodeMap.size <= 1) {
    return nodeMap;
  }
  const newMap = new Map(nodeMap);
  // Find the parent of this node
  const { parentNode, recommendationIndex } = findParentNode(newMap, nodeId);
  if (parentNode) {
    // Remove the node from its parent's recommendation_children_map
    const recommendationChildrenMap =
      parentNode.recommendation_children_map || new Map<number, string[]>();
    const children = recommendationChildrenMap.get(recommendationIndex) || [];
    const updatedChildren = children.filter((id) => id !== nodeId);
    // Update the parent's children list
    recommendationChildrenMap.set(recommendationIndex, updatedChildren);
    // Update the parent node
    const updatedParent = {
      ...parentNode,
      recommendation_children_map: recommendationChildrenMap,
    };
    newMap.set(parentNode.id, updatedParent);
  }
  // Get all descendant node IDs
  const descendantIds = getDescendantNodeIds(newMap, nodeId);
  // Delete the node and all its descendants
  newMap.delete(nodeId);
  descendantIds.forEach((id) => newMap.delete(id));
  return newMap;
};

// Helper function to delete all children of a specific recommendation
const deleteChildrenOfRecommendation = (
  nodeMap: Map<string, KGStepInterface>,
  nodeId: string,
  recommendationIndex: number
): Map<string, KGStepInterface> => {
  const newMap = new Map(nodeMap);
  const node = newMap.get(nodeId);
  if (!node) {
    return newMap;
  }
  // Get all child IDs for this recommendation if the map exists
  const childIds =
    node.recommendation_children_map?.get(recommendationIndex) || [];
  // For each child, delete it and its descendants
  const allNodesToDelete: string[] = [];
  childIds.forEach((childId) => {
    // Get all descendants of this child
    const descendantIds = getDescendantNodeIds(newMap, childId);
    allNodesToDelete.push(childId, ...descendantIds);
  });
  // Create updated node with modifications
  const updatedNode = { ...node };
  
  // Remove the recommendation rule itself first
  if (updatedNode.recommendation_rules) {
    updatedNode.recommendation_rules = updatedNode.recommendation_rules.filter(
      (_, index) => index !== recommendationIndex
    );

    // Then create a new recommendation_children_map with proper indexing
    if (updatedNode.recommendation_children_map) {
      const newRecommendationChildrenMap = new Map<number, string[]>();

      // For each entry in the old map
      Array.from(updatedNode.recommendation_children_map.entries()).forEach(
        ([oldIndex, childIds]) => {
          // Skip the deleted recommendation
          if (oldIndex === recommendationIndex) return;

          // Calculate new index based on position
          const newIndex =
            oldIndex > recommendationIndex ? oldIndex - 1 : oldIndex;
          newRecommendationChildrenMap.set(newIndex, childIds);
        }
      );

      updatedNode.recommendation_children_map = newRecommendationChildrenMap;
    }
    
    // Also update the recommendation_edits_map if it exists
    if (updatedNode.recommendation_edits_map) {
      const newRecommendationEditsMap = new Map<number, any[]>();
      
      // For each entry in the old edits map
      Array.from(updatedNode.recommendation_edits_map.entries()).forEach(
        ([oldIndex, edits]) => {
          // Skip the deleted recommendation
          if (oldIndex === recommendationIndex) return;
          
          // Calculate new index based on position
          const newIndex = 
            oldIndex > recommendationIndex ? oldIndex - 1 : oldIndex;
          newRecommendationEditsMap.set(newIndex, edits);
        }
      );
      
      updatedNode.recommendation_edits_map = newRecommendationEditsMap;
    }
  }

  // Update the node in the map
  newMap.set(nodeId, updatedNode);

  // Delete all the nodes to delete
  allNodesToDelete.forEach((id) => newMap.delete(id));

  return newMap;
};

// Helper function to convert an StepInterface to AnalysisStepProto for API submission
export const convertNodeToKGAnalysisStep = (
  node: KGStepInterface,
  isRootNode: boolean = false,
  nodeMap: Map<string, KGStepInterface> = new Map()
): AnalysisStepProto => {
  let observationEdits: ObservationEditProto[] = [];

  // If this is a root node, we don't add any observation edits
  if (!isRootNode && node.parent_node_id) {
    // For child nodes, generate observation edits by comparing with parent
    const parentNode = nodeMap.get(node.parent_node_id);
    if (parentNode && node.kg_observation && parentNode.kg_observation) {
      const parentObservation = getKGObservation(parentNode?.id, nodeMap);
      const currentObservation = getKGObservation(node?.id, nodeMap);
      // Generate observation edits by comparing this node's observation with its parent's
      observationEdits = ObservationUtils.getObservationEdits(
        parentObservation, currentObservation);
    }
  }

  // Create the analysis step
  const analysisStep = AnalysisStepProto.create({
    id: node.id.toString(),
    step_label: node.label,
    observation_edits: observationEdits,
    recommendation_rules: node.recommendation_rules || [],
    analysis_columns: node?.analysis_columns || [],
  });

  return analysisStep;
};

// New function to build Analysis Plan Proto with KGObservation
export const buildKGAnalysisPlanProto = (
  rootNode: KGStepInterface,
  nodeMap: Map<string, KGStepInterface>,
  selectedMetricHeaders: Header[],
  existingPlanId?: string, // Add optional parameter for existing plan ID
  existingPlanHeader?: Header // Add optional parameter for existing plan header
): AnalysisPlanProto => {  
  // Start with the root node
  const rootStep = convertNodeToKGAnalysisStep(rootNode, true /* isRootStep */, nodeMap);
  // Set the child_step_ids for each recommendation rule in the root node
  if (rootNode.recommendation_rules && rootNode.recommendation_children_map) {
    rootNode.recommendation_rules.forEach((rule, index) => {
      // Get child nodes for this recommendation
      const childIds = rootNode.recommendation_children_map?.get(index) || [];
      // Set the child_step_ids property on the rule
      rootStep.recommendation_rules[index].child_step_ids = childIds;
    });
  }
  // Create KGObservationProto for root observation
  const kgObservation = KGObservationProto.create({
    metrics: rootNode.selected_metrics || [], // Use selected_metrics from root node
    benchmarks: [], // Will populate below
    filters: [], // Will populate below
    columns: [],  // Will populate below
  });
  // For root node, always use selected_* fields for the observation
  if (rootNode.selected_benchmarks && rootNode.selected_benchmarks.length > 0) {
    kgObservation.benchmarks = rootNode.selected_benchmarks;
  }
  if (rootNode.selected_columns && rootNode.selected_columns.length > 0) {
    kgObservation.columns = rootNode.selected_columns;
  }
  if (rootNode.selected_filters && rootNode.selected_filters.length > 0) {
    kgObservation.filters = rootNode.selected_filters;
  }
  // All analysis steps to be added to the plan
  const allSteps: AnalysisStepProto[] = [rootStep];
  // Process all nodes in the nodeMap (except the root which we've already added)
  nodeMap.forEach((node, nodeId) => {
    // Skip the root node as we've already added it
    if (nodeId === rootNode.id) {
      return;
    }
    // Convert the node to an AnalysisStepProto
    const step = convertNodeToKGAnalysisStep(node, false /* isRootNode */, nodeMap);
    // Set the child_step_ids for each recommendation rule in this node
    if (node.recommendation_rules && node.recommendation_children_map) {
      node.recommendation_rules.forEach((rule, index) => {
        // Get child nodes for this recommendation
        const childIds = node.recommendation_children_map?.get(index) || [];
        // Set the child_step_ids property on the rule
        step.recommendation_rules[index].child_step_ids = childIds;
      });
    }
    allSteps.push(step);
  });

  // Create the header based on whether we have an existing one
  let header: Header;
  if (existingPlanHeader) {
    // Use the existing header, preserving its name (especially important for renamed duplicated plans)
    header = Header.create({
      ...existingPlanHeader,
      id: existingPlanId || existingPlanHeader.id,
      // Keep the existing header name, don't override with node label
      name: existingPlanHeader.name || "Multi-Metric Analysis",
    });
  } else {
    // Create a minimal header for a new plan
    header = Header.create({
      id: existingPlanId || uuidv4(),
      name: nodeMap?.get(rootNode?.id)?.label || "Multi-Metric Analysis",
      description: selectedMetricHeaders.length > 1 ? `Analysis of ${selectedMetricHeaders.length} metrics` : "",
      type: MetadataTypeEnum.ANALYSIS_PLAN
    });
  }

  // Return the full analysis plan with KGObservationProto as root_observation
  const analysisPlan = AnalysisPlanProto.create({
    header: header,
    root_observation: kgObservation,
    root_step_id: rootNode.id,
    analysis_steps: allSteps,
  });
  
  return analysisPlan;
};

// Helper function to get the KGObservation from the given KGStepInterface
export const getKGObservation = (
  nodeId: string,
  nodeMap: Map<string, KGStepInterface>
): KGObservationProto => {
  const node = nodeMap.get(nodeId);
  if (!node) {
    // Return an empty observation instead of throwing
    return KGObservationProto.create({
      metrics: [],
      benchmarks: [],
      filters: [],
      columns: [],
    });
  }
  
  // ALWAYS create a fresh KGObservationProto to ensure it's up-to-date
  const kgObservation = KGObservationProto.create({
    metrics: node.selected_metrics || [],
    benchmarks: node.selected_benchmarks || [],
    filters: node.selected_filters || [],
    columns: node.selected_columns || [],
  });
  
  return kgObservation;
};

// Helper function to regenerate all IDs in a plan and node map
const regeneratePlanIds = (
  originalPlan: AnalysisPlanProto,
  originalNodeMap: Map<string, KGStepInterface>
): { newPlan: AnalysisPlanProto; newNodeMap: Map<string, KGStepInterface> } => {
  // Create mapping of old IDs to new IDs
  const idMapping = new Map<string, string>();
  
  // First pass: create new IDs for all steps
  if (originalPlan.analysis_steps) {
    originalPlan.analysis_steps.forEach(step => {
      if (step.id) {
        idMapping.set(step.id, uuidv4());
      }
    });
  }
  
  // Also map the root step ID
  if (originalPlan.root_step_id) {
    if (!idMapping.has(originalPlan.root_step_id)) {
      idMapping.set(originalPlan.root_step_id, uuidv4());
    }
  }
  
  // Second pass: create new plan with updated IDs
  const newAnalysisSteps = originalPlan.analysis_steps?.map(step => {
    const newStepId = idMapping.get(step?.id || "");
    
    // Update recommendation rules to use new child step IDs
    const newRecommendationRules = step.recommendation_rules?.map(rule => ({
      ...rule,
      child_step_ids: rule.child_step_ids?.map(childId => idMapping.get(childId) || childId) || []
    })) || [];
    
    return AnalysisStepProto.create({
      ...step,
      id: newStepId,
      recommendation_rules: newRecommendationRules
    });
  }) || [];
  
  // Create new plan with updated IDs and name
  const newPlan = AnalysisPlanProto.create({
    ...originalPlan,
    header: Header.create({
      id: uuidv4(),
      name: `${originalPlan?.header?.name || "Plan"} - copied`
    }),
    root_observation: KGObservationProto.create({
      metrics: [...(originalPlan?.root_observation?.metrics || [])],
      benchmarks: [...(originalPlan?.root_observation?.benchmarks || [])],
      filters: [...(originalPlan?.root_observation?.filters || [])],
      columns: [...(originalPlan?.root_observation?.columns || [])],
    }),
    root_step_id: idMapping.get(originalPlan?.root_step_id || "") || originalPlan?.root_step_id,
    analysis_steps: newAnalysisSteps
  });
  
  // Create new node map with updated IDs and deep copies of all data
  const newNodeMap = new Map<string, KGStepInterface>();
  
  originalNodeMap.forEach((node, oldId) => {
    const newId = idMapping.get(oldId) || oldId;
    
    // Update recommendation children map with new IDs
    const newRecommendationChildrenMap = new Map<number, string[]>();
    if (node.recommendation_children_map) {
      node.recommendation_children_map.forEach((childIds, index) => {
        const newChildIds = childIds.map(childId => idMapping.get(childId) || childId);
        newRecommendationChildrenMap.set(index, newChildIds);
      });
    }
    
    // Update parent node ID if it exists
    const newParentNodeId = node.parent_node_id ? idMapping.get(node.parent_node_id) || node.parent_node_id : undefined;
    
    // Update recommendation edits map if it exists
    const newRecommendationEditsMap = new Map<number, any[]>();
    if (node.recommendation_edits_map) {
      node.recommendation_edits_map.forEach((edits, index) => {
        newRecommendationEditsMap.set(index, edits);
      });
    }
    
    // Create deep copies of all arrays and objects to prevent shared references
    const newNode: KGStepInterface = {
      ...node,
      id: newId,
      parent_node_id: newParentNodeId,
      recommendation_children_map: newRecommendationChildrenMap,
      recommendation_edits_map: newRecommendationEditsMap,
      
      // Create deep copies of all shared data structures
      metrics: node.metrics ? [...node.metrics] : [],
      benchmarks: node.benchmarks ? [...node.benchmarks] : [],
      non_benchmarks: node.non_benchmarks ? [...node.non_benchmarks] : [],
      temporal_columns: node.temporal_columns ? [...node.temporal_columns] : [],
      
      // Create deep copies of selected items
      selected_benchmarks: node.selected_benchmarks ? [...node.selected_benchmarks] : [],
      selected_columns: node.selected_columns ? [...node.selected_columns] : [],
      selected_filters: node.selected_filters ? [...node.selected_filters] : [],
      
      // Create deep copies of other arrays
      analysis_columns: node.analysis_columns ? [...node.analysis_columns] : [],
      recommendation_rules: node.recommendation_rules ? [...node.recommendation_rules] : [],
      recommendation_edits: node.recommendation_edits ? [...node.recommendation_edits] : [],
      
      // Create a deep copy of the KG observation if it exists
      kg_observation: node.kg_observation ? KGObservationProto.create({
        metrics: node.kg_observation.metrics ? [...node.kg_observation.metrics] : [],
        benchmarks: node.kg_observation.benchmarks ? [...node.kg_observation.benchmarks] : [],
        filters: node.kg_observation.filters ? [...node.kg_observation.filters] : [],
        columns: node.kg_observation.columns ? [...node.kg_observation.columns] : [],
      }) : null
    };
    
    newNodeMap.set(newId, newNode);
  });
  
  return { newPlan, newNodeMap };
};

export const applyRecommendationEdits = (recommendationEdits: RecommendationProto[], currentNode: KGStepInterface) => {
  let childSelectedColumns = [...(currentNode.selected_columns || [])];
  let childSelectedMetrics = [...(currentNode.selected_metrics || [])];
  let childSelectedBenchmarks = [...(currentNode.selected_benchmarks || [])];
  let childSelectedFilters = [...(currentNode.selected_filters || [])];

  // Apply recommendation edits to modify selections.
  for (const edit of recommendationEdits) {
    // Handle structured edits
    if (edit.edits) {
      for (const observationEdit of edit.edits) {
        switch (observationEdit.edit_type) {
          case ObservationEditType.ADD_COLUMN:
            if (observationEdit.column && !childSelectedColumns.some(col => 
                col.column_id === observationEdit.column?.column_id)) {
              childSelectedColumns.push(observationEdit.column);
            }
            break;
          case ObservationEditType.ADD_METRIC:
            if (observationEdit.metric && !childSelectedMetrics.some(m => 
                m.header?.id === observationEdit.metric?.header?.id)) {
              childSelectedMetrics.push(observationEdit.metric);
            }
            break;
          case ObservationEditType.ADD_BENCHMARK:
            if (observationEdit.benchmark && !childSelectedBenchmarks.some(b => 
                b.header?.id === observationEdit.benchmark?.header?.id)) {
              childSelectedBenchmarks.push(observationEdit.benchmark);
            }
            break;
          case ObservationEditType.REMOVE_COLUMN:
            if (observationEdit.column) {
              childSelectedColumns = childSelectedColumns.filter(col => col.column_id !== observationEdit.column?.column_id);
            }
            break;
          case ObservationEditType.REMOVE_METRIC:
            if (observationEdit.metric) {
              childSelectedMetrics = childSelectedMetrics.filter(m => m.header?.id !== observationEdit.metric?.header?.id);
            }
            break;
          case ObservationEditType.REMOVE_BENCHMARK:
            if (observationEdit.benchmark) {
              childSelectedBenchmarks = childSelectedBenchmarks.filter(b => b.header?.id !== observationEdit.benchmark?.header?.id);
            }
            break;
        }
      }
    }
    
    // Handle select_anyone field (legacy support)
    if (edit?.select_anyone) {
      edit.select_anyone.forEach(columnId => {
        const metric = currentNode?.metrics?.find(m => m.header?.id === columnId);
        if (metric && !childSelectedMetrics.some(m => m.header?.id === columnId)) {
          childSelectedMetrics.push(metric);
        }
        
        const column = currentNode?.non_benchmarks?.find(col => col.column_id === columnId);
        if (column && !childSelectedColumns.some(col => col.column_id === columnId)) {
          childSelectedColumns.push(column);
        }
      });
    }
  }
  return {
    childSelectedColumns,
    childSelectedMetrics,
    childSelectedBenchmarks,
    childSelectedFilters,
  };
};

// Helper function to generate all column hash combinations (for recommendations)
export const generateAllColumnHashes = (
  currentNode: KGStepInterface,
  selectedColumns: ObservationColumnProto[],
  selectedMetrics: MetricProto[],
  selectedBenchmarks: BenchmarkEntityProto[],
  analysisColumns: ColumnHashProto[]
): ColumnHashProto[] => {
  if (!currentNode) return [];
  
  const allColumnHashes: ColumnHashProto[] = [];
  
  // Get all column IDs (from both selected columns and metrics)
  const columnIds: string[] = [];
  
  selectedColumns.forEach(column => {
    if (column.column_id 
      && column.kg_column_header?.column_type !== ColumnType.ATTRIBUTE
      && column.kg_column_header?.column_type !== ColumnType.TEMPORAL) {
      columnIds.push(column.column_id);
    }
  });
  
  selectedMetrics?.forEach(metric => {
    if (metric.header?.id) {
      columnIds.push(metric.header.id);
    }
  });
  
  // Add basic column hashes (without benchmarks, without analysis types)
  columnIds.forEach(columnId => {
    allColumnHashes.push(
      ColumnHashProto.create({
        actuals_id: columnId
      })
    );
  });
  
  // Add column+benchmark combinations (basic and with analysis types)
  if (selectedBenchmarks.length > 0) {
    columnIds?.forEach(columnId => {
      selectedBenchmarks?.forEach(benchmark => {
        if (benchmark.header?.id) {
          // Basic column+benchmark combination (no analysis type)
          allColumnHashes.push(
            ColumnHashProto.create({
              actuals_id: columnId,
              benchmark_id: benchmark.header?.id
            })
          );
          
          // Add combinations with analysis types if they exist
          const existingAnalysisTypes = new Set<AnalysisColumnType>();
          analysisColumns?.forEach(col => {
            if (col.benchmark_id === benchmark.header?.id && 
                col.analysis_column_type !== undefined) {
              existingAnalysisTypes.add(col.analysis_column_type);
            }
          });
          
          existingAnalysisTypes?.forEach(analysisType => {
            allColumnHashes.push(
              ColumnHashProto.create({
                actuals_id: columnId,
                benchmark_id: benchmark.header?.id,
                analysis_column_type: analysisType
              })
            );
          });
        }
      });
    });
  }
  
  return allColumnHashes;
};

// Helper function to fetch MKG.
export const fetchMKG = async (metricId: string): Promise<
{ mkg: MetricProto | null }> => {
  // Fetch MKG.
  const response = await getMetricById(metricId, true /* Include edges */);
  if (!response || !response?.header) {
    showToast(`Failed to fetch metric with ID: ${metricId}`, "", "error");
    return { mkg: null };
  }
  return { mkg: response };
};

// Helper function to get the metrics with edges.
export const fetchMetricsWithEdges = async (metricIds: string[]): Promise<MetricProto[]> => {
  const metricPromises = metricIds.map(id => fetchMKG(id));
  const metricResults = await Promise.all(metricPromises);
  return metricResults?.map(result => result?.mkg)
         .filter((data): data is MetricProto => data !== null);
};

export const KGAnalysisPlanContextProvider: React.FC<{
  children: React.ReactNode;}> = ({ children }) => {
  const [plan, setPlan] = useState<KGStepInterface | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<Map<string, MetricProto>>(new Map());
  const [models, setModels] = useState<Map<string, ModelInfo>>(new Map());
  const [nodeMap, setNodeMap] = useState<Map<string, KGStepInterface>>(new Map());
  const [selectedMetricHeader, setSelectedMetricHeader] =
    useState<Header[]>([]);
  const [metricList, setMetricList] = useState<Header[]>([]);
  const [currentPlanId, setCurrentPlanId] = useState<string | undefined>(undefined);
  const [currentPlanHeader, setCurrentPlanHeader] = useState<Header | undefined>(undefined);
  const [observationData, setObservationData] = useState<any | null>(null);
  const [openRecommendationNodeId, setOpenRecommendationNodeId] = useState<string | null>(null);
  const configurator = useDisclosure();
  
  // Access KGBatchContext
  const { 
    selectedStepIndex,
    createJobStepWithPlan,
    selectedJobId,
    setSelectedJobId,
    setSelectedJob,
    setSelectedStepIndex,
    jobHeaders,
    setJobHeaders,
    setShowStepsContainer,
    analysisActionRequests,
    getQnAActionsForStep
  } = useKGBatchContext();

  // Update a node in the flat map and in the plan if it's the root
  const updateNodeHandler = (updatedNode: KGStepInterface) => {
    // We'll let KGNodeConfigurator handle the kg_observation refresh
    const newNodeMap = updateNode(nodeMap, updatedNode);
    // If we're updating the root node, track filter changes
    if (plan && plan.id === updatedNode.id) {
      // Extra safety check to ensure metrics are preserved
      if (!updatedNode.metrics && plan.metrics) {
        updatedNode.metrics = plan.metrics;
      }
      setPlan(updatedNode);
    }
    setNodeMap(newNodeMap);
    
    // Auto-save is triggered by useEffect hooks in components watching state changes
  };

  // Generic node save function - can be used for both auto-save and manual save
  const handleNodeSave = useCallback((
    nodeId: string,
    nodeInfo: {
      nodeName: string;
      selectedColumns: ObservationColumnProto[];
      selectedBenchmarks: BenchmarkEntityProto[];
      filters: FilterProto[];
      analysisColumns: ColumnHashProto[];
      recommendationRules: RecommendationRuleProto[];
      selectedMetrics?: MetricProto[];
    },
    latestRecommendations?: RecommendationRuleProto[],
    updatedNode?: KGStepInterface
  ): KGStepInterface | null => {
    const currentNode = nodeMap.get(nodeId);
    if (!currentNode) return null;

    // Use selectedMetrics if provided, otherwise fall back to current node selected metrics
    const metrics = nodeInfo.selectedMetrics || currentNode.selected_metrics || [];
    
    // Create KG observation for the current node
    const kgObservation = KGObservationProto.create({
      metrics: [...metrics],
      benchmarks: [...nodeInfo.selectedBenchmarks],
      filters: [...nodeInfo.filters],
      columns: [...nodeInfo.selectedColumns],
    });
    
    // If updatedNode is provided, use it directly
    if (updatedNode) {
      // Just update the recommendations if they were provided
      if (latestRecommendations) {
        updatedNode.recommendation_rules = [...latestRecommendations];
      }
      
      // Make sure we don't lose analysis_columns when updating
      if (nodeInfo.analysisColumns?.length && !updatedNode.analysis_columns?.length) {
        updatedNode.analysis_columns = [...nodeInfo.analysisColumns];
      }
      
      updateNodeHandler(updatedNode);
      return updatedNode;
    }
    
    // Update the node with the new data
    const updatedStep: KGStepInterface = {
      ...currentNode,
      label: nodeInfo.nodeName,
      
      // Preserve available options
      metrics: [...(currentNode.metrics || [])],
      benchmarks: [...(currentNode?.benchmarks || [])],
      non_benchmarks: [...(currentNode?.non_benchmarks || [])],
      temporal_columns: [...(currentNode?.temporal_columns || [])],
      
      // Update selected values
      selected_metrics: [...metrics],
      selected_columns: [...nodeInfo.selectedColumns],
      selected_benchmarks: [...nodeInfo.selectedBenchmarks],
      selected_filters: [...nodeInfo.filters],
      
      // Update analysis columns
      analysis_columns: [...nodeInfo.analysisColumns],
      
      // Preserve parent relationship
      parent_node_id: currentNode?.parent_node_id,
      
      // Update recommendations - use latest recommendations if provided
      recommendation_rules: latestRecommendations ? [...latestRecommendations] : [...nodeInfo.recommendationRules],
      
      // Preserve recommendation maps (deep copy)
      recommendation_children_map: new Map(currentNode.recommendation_children_map),
      recommendation_edits_map: new Map(currentNode.recommendation_edits_map),
      
      // Preserve recommendation_edits (deep copy)
      recommendation_edits: [...(currentNode.recommendation_edits || [])],
      
      // Set the KG observation
      kg_observation: kgObservation,
    };
    
    // Update the node
    updateNodeHandler(updatedStep);
    
    return updatedStep;
  }, [nodeMap, updateNodeHandler]);

  // Add a child to a recommendation
  const addChildToRecommendationHandler = (
    nodeId: string,
    recommendationIndex: number,
    newChild: KGStepInterface
  ) => {
    const newNodeMap = addChildToRecommendation(
      nodeMap,
      nodeId,
      recommendationIndex,
      newChild
    );
    setNodeMap(newNodeMap);
  };

  // Update the node map with a new or updated node
  const updateNodeMapHandler = (node: KGStepInterface) => {
    setNodeMap((prev) => {
      const newMap = new Map(prev);
      newMap.set(node.id, node);
      return newMap;
    });
  };

  // Delete a node and its entire subtree
  const deleteNodeHandler = (nodeId: string) => {
    // Cannot delete the root node
    if (plan && plan.id === nodeId) {
      return;
    }

    // Close the configurator if the deleted node was selected
    if (selectedNodeId === nodeId) {
      configurator.onClose();
      setSelectedNodeId(null);
    }

    const newNodeMap = deleteNode(nodeMap, nodeId);
    setNodeMap(newNodeMap);
  };

  // Delete all children of a specific recommendation
  const deleteChildrenOfRecommendationHandler = (
    nodeId: string,
    recommendationIndex: number
  ) => {
    const newNodeMap = deleteChildrenOfRecommendation(
      nodeMap,
      nodeId,
      recommendationIndex
    );
    setNodeMap(newNodeMap);
  };

  const executeKGAnalysisPlanHandler = async () => {
    if (!plan || selectedMetricHeader.length === 0) {
      showToast("Missing plan or selected metric", "", "error");
      return;
    }
    // Get the most current version of the root node from nodeMap to ensure we have latest changes
    const currentRootNode = nodeMap.get(plan.id) || plan;
    const proto = buildKGAnalysisPlanProto(currentRootNode, nodeMap, selectedMetricHeader, currentPlanId, currentPlanHeader);
    const transactionId = uuidv4();
    const requestBuilder = new StatefulRequestBuilder(transactionId);
  
    const statefulRequest: StatefulAnalysisTreeRequest[] = [];
    const statefulAnalysisTreeRequest = StatefulAnalysisTreeRequest.create({
      type: AnalysisTreeRequestType.GENERATE_TREE,
      generate_tree: GenerateTree_Request.create({
        analysis_plan: proto,
      }),
    });
    statefulRequest.push(statefulAnalysisTreeRequest);

    // Get questions from QnA actions for the current selected step
    let questions: string[] = [];
    if (selectedStepIndex !== null && analysisActionRequests[selectedStepIndex]?.step_id) {
      const stepId = analysisActionRequests[selectedStepIndex].step_id!;
      const qnaActions = getQnAActionsForStep(stepId);
      if (qnaActions.length > 0) {
        // Collect all questions from all QnA actions for this step
        questions = qnaActions.flatMap((action: ActionRequest) => action.qna_request?.questions || []);
      }
    }

    if (questions.length > 0) {
      const setCommentaryRequest: CommentaryProto[] = [];
      setCommentaryRequest.push(CommentaryProto.create({
        questions: questions,
      }));
      statefulRequest.push(requestBuilder.SetCommentaryRequest(setCommentaryRequest));
    }

    const response: StatefulResponse = await statefulAnalysisTree(
      requestBuilder.createStatefulRequest(statefulRequest));
    if (response) {
      window.open(`/analyze?transaction_id=${transactionId}`, "_blank");
      return response;
    }
  };

  const getKGObservationFromKGStepInterface = (
    nodeId: string
  ): KGObservationProto => {
    return getKGObservation(nodeId, nodeMap);
  };
  
  const saveKGAnalysisPlanHandler = async () => {
    if (!plan || selectedMetricHeader.length === 0) {
      showToast("Missing plan or selected metric", "", "error");
      return;
    }
    // Get the most current version of the root node from nodeMap to ensure we have latest changes
    const currentRootNode = nodeMap.get(plan.id) || plan;
    const proto = buildKGAnalysisPlanProto(currentRootNode, nodeMap, selectedMetricHeader, currentPlanId, currentPlanHeader);
    const response = await createOrUpdatePlan(proto);
    if (response) {
      showToast("Plan saved successfully", "", "success");
      // Update current plan ID and header if this was a new plan
      if (!currentPlanId) {
        setCurrentPlanId(proto.header?.id);
        setCurrentPlanHeader(proto.header);
      } else {
        // Even for existing plans, update the current header to reflect any changes
        setCurrentPlanHeader(proto.header);
      }
      // After successful plan save, update or create job/step with the plan header
      if (selectedJobId && proto?.header) {
        // Check if there's a selected step to update
        if (selectedStepIndex === null) {
          // Create a new step with the plan header
          await createJobStepWithPlan(proto.header);
        } 
      } else {
        // No job selected - create a new job with a step containing this plan
        const jobName = "New Job";
        // Create a new step with the plan header
        const newStep = AnalysisActionRequest.create({
          step_id: uuidv4(),
          analysis_plan_header: proto.header,
        });
        // Create a job request with the new step
        const jobRequest = JobRequest.create({
          analysis_action_requests: [newStep],
        });
        // Create a new job with the step already included
        const newJobResponse = await createOrUpdateJobApi({
          name: jobName,
          request: jobRequest,
        });
        if (newJobResponse && newJobResponse.header) {
          // Add the new job to the list of jobs
          setJobHeaders([...jobHeaders, newJobResponse.header]);
          // Set the new job as selected
          setSelectedJobId(newJobResponse.header.id!);
          setSelectedJob(newJobResponse);
          // Set the new step as selected (index 0 since it's the only step)
          setSelectedStepIndex(0);
          // Show the steps container to display the newly created step
          setShowStepsContainer(true);
          showToast("Created new job with plan", "", "success");
        } else {
          showToast("Failed to create new job", "", "error");
        }
      }
    }
  };
  

  // Handler to load a plan from JSON
  const handleLoadPlan = async (action: AnalysisActionRequest) => {
    if (!action?.analysis_plan_header) {
      showToast("No plan header provided.", "", "warning");
      return;
    }
    const loadedPlan = await getPlan(action?.analysis_plan_header?.id!);
    if (!loadedPlan) {
      return;
    }
    // Store the plan ID and header from the action (not the loaded plan)
    // This ensures we track the correct plan ID, especially for duplicated plans
    setCurrentPlanId(action.analysis_plan_header.id);
    setCurrentPlanHeader(action.analysis_plan_header);
    // Get all metric IDs from the plan's root observation
    const metricIds: Set<string> = new Set();
    // Check for metrics in root_observation
    if (loadedPlan.root_observation?.metrics) {
      loadedPlan.root_observation.metrics.forEach(metric => {
        if (metric.header?.id) {
          metricIds.add(metric.header.id);
        }
      });
    }
    // If no metrics found in root_observation, try looking for a single metric
    if (metricIds.size === 0 && loadedPlan.metric?.header?.id) {
      metricIds.add(loadedPlan.metric.header.id);
    }
    
    // If still no metrics found, show error
    if (metricIds.size === 0) {
      showToast("Plan has no valid metrics", "", "error");
      return;
    }
    const mkgs = await fetchMetricsWithEdges(Array.from(metricIds));
    const metricsMap = new Map<string, MetricProto>(metrics);
    mkgs?.forEach(metric => {
      metricsMap.set(metric.header?.id!, metric);
    });
    setMetrics(metricsMap);
    // Extract headers from mkgs.
    const metricHeaders = mkgs?.map(data => data.header)
      ?.filter((header): header is Header => header !== undefined);
    
    // Update context with selected metric headers
    setSelectedMetricHeader(metricHeaders);
    
    // Build node map from the plan using all fetched metrics
    const { nodeMap: newNodeMap, rootNode } = await buildNodeMapFromPlan(loadedPlan, mkgs);
    
    // Update plan and node map in context
    setPlan(rootNode);
    setNodeMap(newNodeMap);
  };

  // Function to duplicate a plan by ID
  const duplicatePlan = async (planId: string): Promise<Header | null> => {  
      // Load the original plan
      const originalPlan = await getPlan(planId);
      if (!originalPlan) {
        return null;
      }
      // Get all metric IDs from the plan's root observation
      const metricIds: Set<string> = new Set();
      // Check for metrics in root_observation
      if (originalPlan.root_observation?.metrics) {
        originalPlan.root_observation.metrics.forEach(metric => {
          if (metric.header?.id) {
            metricIds.add(metric.header.id);
          }
        });
      }      
      // If still no metrics found, show error
      if (metricIds.size === 0) {
        showToast("Plan has no valid metrics", "", "error");
        return null;
      }
      const mkgs = await fetchMetricsWithEdges(Array.from(metricIds));
      // Extract headers from mkgs.
      const metricHeaders = mkgs?.map(data => data.header)
        ?.filter((header): header is Header => header !== undefined);
      
      // Update context with selected metric headers
      setSelectedMetricHeader(metricHeaders);
      // Build node map from the original plan
      const { nodeMap: originalNodeMap } = await buildNodeMapFromPlan(originalPlan, mkgs);

      // Regenerate IDs for the plan and node map
      const { newPlan, newNodeMap } = regeneratePlanIds(originalPlan, originalNodeMap);
      // Save the new plan
      const savedPlan = await createOrUpdatePlan(newPlan);
      if (savedPlan && newPlan.header && newNodeMap) {
        // Update the context to track the new duplicated plan
        setCurrentPlanId(newPlan.header.id);
        setCurrentPlanHeader(newPlan.header);
        
        // Set the plan and nodeMap in the context
        if (newNodeMap?.get(newPlan?.root_step_id!)) {
          setPlan(newNodeMap?.get(newPlan?.root_step_id!)!);
          setNodeMap(newNodeMap);
        }
        
        showToast("Plan duplicated successfully", "", "success");
        return newPlan?.header;
      } else {
        showToast("Failed to save duplicated plan", "", "error");
        return null;
      }
    showToast("Failed to duplicate plan", "", "error");
    return null;
  };

  // When the tree changes, make sure it's in the nodeMap
  useEffect(() => {
    if (plan) {
      updateNodeMapHandler(plan);
    }
  }, [plan]);

  // Function to get data for a specific node
  const handleGetDataForNode = async (nodeId: string) => {
    const node = nodeMap.get(nodeId);
    if (!node) {
      showToast("Node not found", "", "error");
      return;
    }
    // Create KG observation for the node
    const kgObservation = KGObservationProto.create({
      metrics: node?.selected_metrics || [],
      benchmarks: node?.selected_benchmarks || [],
      filters: node?.selected_filters || [],
      columns: node?.selected_columns || []
    });

    const request = AnalysisTreeNodeProto.create({
      id: node.id,
      node_label: node.label,
      node_depth: node.node_depth,
      kg_observation: kgObservation,
      analysis_columns: node?.analysis_columns || [],
    });

    // Call the API to get observation data
    const response = await getNodeData(request);
    
    if (response) {
      // Create NodeInsights structure that DataTableMKG expects
      setObservationData({current_node: response});
    } else {
      showToast("Failed to fetch observation data", "", "error");
    }
  };

  const generateAllColumnHashesHandler = (
    currentNode: KGStepInterface,
    selectedColumns: ObservationColumnProto[],
    selectedMetrics: MetricProto[],
    selectedBenchmarks: BenchmarkEntityProto[],
    analysisColumns: ColumnHashProto[]
  ) => generateAllColumnHashes(currentNode, selectedColumns, selectedMetrics, selectedBenchmarks, analysisColumns);
  
  // Add a child node to a recommendation
  const handleAddChildNode = async (recommendationIndex: number, currentNode: KGStepInterface) => {
    if (!currentNode) return;

    // Get recommendation edits and initialize child selections.
    const recommendationEdits = currentNode.recommendation_edits_map?.get(recommendationIndex) || [];

    let { 
      childSelectedColumns,
      childSelectedMetrics,
      childSelectedBenchmarks,
      childSelectedFilters 
    } = applyRecommendationEdits(recommendationEdits, currentNode);

    childSelectedMetrics = await Promise.all(childSelectedMetrics?.map(async (metric) => {
      if (!metric.edges || metric.edges.length === 0) {
        const updatedMetric = await getMetricById(metric.header?.id!, true /* include edges */);
        return updatedMetric || metric;
      }
      return metric;
    }));
    
    let processedEdges: {
      attributeHeaders: Header[];
      measureHeaders: Header[];
      metricEdgeHeaders: Header[];
      benchmarkEntities: BenchmarkEntityProto[];
      temporalHeaders: Header[];
      metricsHeaders: Header[];
    } = {
      attributeHeaders: [],
      measureHeaders: [],
      metricEdgeHeaders: [],
      benchmarkEntities: [],
      temporalHeaders: [],
      metricsHeaders: [],
    };
    // Calculate available options based on selected metrics
    if (childSelectedMetrics.length > 0) {
      processedEdges = mergeMetricEdgesByType(childSelectedMetrics);
    } else {
      processedEdges = mergeMetricEdgesByType(currentNode?.metrics || []);
    }
    // Get columns from processed edges
    const attributeColumns = ObservationUtils.getObservationColumns(processedEdges?.attributeHeaders) || [];
    const measureColumns = ObservationUtils.getObservationColumns(processedEdges?.measureHeaders) || [];
    const temporalColumns = ObservationUtils.getObservationColumns(processedEdges?.temporalHeaders) || [];

    // Filter parent's non_benchmarks to keep only those whose kg_column_header.id matches processed edges
    const retainedAttributeColumns = currentNode?.non_benchmarks?.filter(column =>
      processedEdges?.attributeHeaders?.some(header => header.id === column.kg_column_header?.id)
    ) || [];
    
    const retainedMeasureColumns = currentNode?.non_benchmarks?.filter(column =>
      processedEdges?.measureHeaders?.some(header => header.id === column.kg_column_header?.id)
    ) || [];
    
    const retainedTemporalColumns = currentNode?.non_benchmarks?.filter(column =>
      processedEdges?.temporalHeaders?.some(header => header.id === column.kg_column_header?.id)
    ) || [];

    // Add new columns from processed edges that don't exist in retained columns
    const newAttributeColumns = attributeColumns.filter(newCol =>
      !retainedAttributeColumns.some(retained => retained.kg_column_header?.id === newCol.kg_column_header?.id)
    );
    
    const newMeasureColumns = measureColumns.filter(newCol =>
      !retainedMeasureColumns.some(retained => retained.kg_column_header?.id === newCol.kg_column_header?.id)
    );
    
    const newTemporalColumns = temporalColumns.filter(newCol =>
      !retainedTemporalColumns.some(retained => retained.kg_column_header?.id === newCol.kg_column_header?.id)
    );

    // Combine retained and new columns
    const availableAttributeColumns = [...retainedAttributeColumns, ...newAttributeColumns];
    const availableMeasureColumns = [...retainedMeasureColumns, ...newMeasureColumns];
    const availableTemporalColumns = [...retainedTemporalColumns, ...newTemporalColumns];   
    
    // Include both processed benchmarks and growth benchmarks.
    const availableBenchmarks = [...(processedEdges?.benchmarkEntities || [])];
    // Push growth benchmarks to available benchmarks.
    currentNode?.benchmarks?.forEach(benchmark => {
      if (benchmark.growth_benchmark) {
        availableBenchmarks.push(benchmark);
      }
    });

    // Filter selections to only include available options.
    const allAvailableColumns = [
      ...availableAttributeColumns,
      ...availableMeasureColumns,
      ...availableTemporalColumns
    ];
    
    // Remove columns that are not available but selected in parent node.
    childSelectedColumns = childSelectedColumns.filter(col =>
      allAvailableColumns.some(c => c.column_id === col.column_id)
    );
    
    // Remove benchmarks that are not available but selected in parent node.
    childSelectedBenchmarks = childSelectedBenchmarks.filter(benchmark => {
      if (benchmark.growth_benchmark) return true;
      return availableBenchmarks.some(b => b.header?.id === benchmark?.header?.id);
    });

    // Generate all column hash combinations (including basic ones for recommendations)
    const allParentColumnHashes = generateAllColumnHashes(currentNode, childSelectedColumns, childSelectedMetrics, childSelectedBenchmarks, currentNode?.analysis_columns || []);
    
    // Generate valid analysis columns for child node based on selected metrics and benchmarks
    const validAnalysisColumns: ColumnHashProto[] = [];
    const childBenchmarkIds = new Set<string>();
    
    // Collect benchmark IDs from selected benchmarks
    childSelectedBenchmarks?.forEach(benchmark => {
      if (benchmark.header?.id) {
        childBenchmarkIds.add(benchmark.header.id);
      }
    });
    
    // Get all column IDs for child node
    const childColumnIds = new Set<string>();
    childSelectedColumns.forEach(col => {
      if (col.column_id) childColumnIds.add(col.column_id);
    });
    childSelectedMetrics?.forEach(metric => {
      if (metric.header?.id) childColumnIds.add(metric.header.id);
    });
    
    // Only include analysis columns (those with analysis_column_type) that match child selections
    allParentColumnHashes?.forEach(columnHash => {
      if (columnHash?.analysis_column_type && 
          columnHash?.actuals_id && 
          childColumnIds?.has(columnHash.actuals_id) &&
          (!columnHash?.benchmark_id || childBenchmarkIds?.has(columnHash.benchmark_id))) {
        validAnalysisColumns.push(columnHash);
      }
    });

    // Create and add child node
    const newChild: KGStepInterface = {
      id: uuidv4(),
      label: `New Child Node ${nodeMap.size + 1}`,
      type: "transform",
      parent_node_id: currentNode.id,
      
      // Available options for child node
      metrics: [...(currentNode?.metrics || [])],
      benchmarks: [...availableBenchmarks],
      non_benchmarks: [...allAvailableColumns],
      temporal_columns: [...availableTemporalColumns],
      
      // Selected values after applying edits
      selected_metrics: [...childSelectedMetrics],
      selected_columns: [...childSelectedColumns],
      selected_benchmarks: [...childSelectedBenchmarks],
      selected_filters: [...childSelectedFilters],
      analysis_columns: validAnalysisColumns,
      
      // Initialize empty recommendation data
      recommendation_rules: [],
      recommendation_children_map: new Map(),
      recommendation_edits_map: new Map(),
      recommendation_edits: [...(currentNode.recommendation_edits || []), ...recommendationEdits],
      kg_observation: null,
    };

    // Add child to the recommendation
    addChildToRecommendationHandler(currentNode?.id, recommendationIndex, newChild);
  };
  
  // Recommendation popover state handlers
  const openRecommendationPopover = (nodeId: string) => {
    setOpenRecommendationNodeId(nodeId);
  };

  const closeRecommendationPopover = () => {
    setOpenRecommendationNodeId(null);
  };

  const getDescendantNodeIdsHandler = (rootId: string): string[] => {
    return getDescendantNodeIds(nodeMap, rootId);
  };

  return (
    <KGAnalysisPlanContext.Provider
      value={{
        plan,
        setPlan,
        selectedNodeId,
        setSelectedNodeId,
        configurator,
        updateNode: updateNodeHandler,
        addChildToRecommendation: addChildToRecommendationHandler,
        deleteNode: deleteNodeHandler,
        deleteChildrenOfRecommendation: deleteChildrenOfRecommendationHandler,
        nodeMap,
        setNodeMap,
        updateNodeMap: updateNodeMapHandler,
        metrics,
        setMetrics,
        models,
        setModels,
        selectedMetricHeader,
        setSelectedMetricHeader,
        metricList,
        executeKGAnalysisPlanHandler,
        setMetricList,
        getKGObservationFromKGStepInterface,
        handleLoadPlan,
        saveKGAnalysisPlanHandler,
        duplicatePlan,
        handleNodeSave,
        observationData,
        setObservationData,
        currentPlanHeader,
        setCurrentPlanHeader,
        handleGetDataForNode,
        generateAllColumnHashes: generateAllColumnHashesHandler,
        handleAddChildNode,
        openRecommendationNodeId,
        setOpenRecommendationNodeId,
        openRecommendationPopover,
        closeRecommendationPopover,
        getDescendantNodeIds: getDescendantNodeIdsHandler,
        selectedStepIndex,
        analysisActionRequests,
      }}
    >
      {children}
    </KGAnalysisPlanContext.Provider>
  );
};
